/**
 * Miscellaneous utility helpers shared across ISG apps.
 */

/**
 * Debounce a function by the given delay in ms.
 */
export function debounce(fn, delay = 300) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Escape HTML entities to prevent XSS when inserting user content.
 *
 * 🔴 ESCAPES QUOTES. IT DID NOT UNTIL 2026-08-18, AND THAT MATTERED.
 *
 * This was `div.textContent = str; return div.innerHTML` — the browser's own
 * text serialiser. That escapes `&`, `<` and `>` and deliberately NOT `"`,
 * because inside TEXT content a quote is just a quote. Measured:
 *
 *     a"b<c>&d   ->   a"b&lt;c&gt;&amp;d        (the quote passes through)
 *
 * Correct for text. Unsafe in an ATTRIBUTE, which is where the estate mostly
 * uses it: `title="${escapeHtml(x)}"` let a payload close the attribute and
 * add its own. Counted the day this changed: **149 uses in attribute position**
 * across isg-admin, isg-bi, isg-boardroom and others — every one relying on a
 * guarantee this function did not give.
 *
 * Found while fixing a stored XSS in the intranet Documents page, whose author
 * had (correctly) refused to use this helper for attribute values and reached
 * for a local one instead. That is the tell: when callers start avoiding the
 * shared helper, the shared helper is wrong.
 *
 * ⚠️ Now an explicit replacement chain, not a DOM round-trip. `&` MUST be
 * replaced first or it double-escapes the entities the later rules introduce.
 * It also means this works without a DOM, so it is testable in node — the
 * previous implementation could only be exercised in a browser, which is part
 * of why the gap survived.
 *
 * ⚠️ `decodeHtmlEntities` below is unchanged and stays symmetric: it uses the
 * browser's own decoder, which already handles `&quot;` and `&#39;`.
 *
 * ⚠️ The falsy guard is preserved deliberately. `escapeHtml(0)` returns '' and
 * always has; several call sites lean on it for null/undefined. Changing that
 * is a separate decision from closing an escaping hole.
 */
export function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Decode HTML entities (e.g. &amp; → &).
 * Useful for values pre-encoded by CMS or database storage.
 */
export function decodeHtmlEntities(str) {
  if (!str) return '';
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
}

/**
 * Generate a short unique ID (good for DOM IDs, toast IDs, etc.).
 */
export function uid() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

/**
 * Simple DOM element creator.
 *
 *   el('div', { className: 'card', onClick: handler }, [
 *     el('h3', { textContent: 'Title' }),
 *   ]);
 */
export function el(tag, attrs = {}, children = []) {
  const element = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (key === 'className') {
      element.className = value;
    } else if (key === 'innerHTML') {
      element.innerHTML = value;
    } else if (key === 'textContent') {
      element.textContent = value;
    } else if (key.startsWith('on')) {
      element.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (key === 'dataset') {
      Object.assign(element.dataset, value);
    } else {
      element.setAttribute(key, value);
    }
  }
  for (const child of [].concat(children)) {
    if (typeof child === 'string') {
      element.appendChild(document.createTextNode(child));
    } else if (child instanceof Node) {
      element.appendChild(child);
    }
  }
  return element;
}

/**
 * Wait for the given number of milliseconds (Promise-based).
 */
export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
